// src/components/shared/CustomerDetailsModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Gift,
  Sparkles,
  CreditCard,
  Loader2,
  ShoppingBag,
  TrendingUp,
  Receipt,
  Clock,
  IndianRupee,
  ChevronRight,
  Plus,
  Minus,
  Award,
  StickyNote,
  Edit2,
  UtensilsCrossed,
  Coins,
} from "lucide-react";
import customerService from "@/services/customerService";

export default function CustomerDetailsModal({
  isOpen,
  onClose,
  customer,
  onEdit,
  showEditButton = true,
}) {
  const [customerProfile, setCustomerProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  //LOYALTY PROGRAM
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltySummary, setLoyaltySummary] = useState(null);
  const [loyaltyTransactions, setLoyaltyTransactions] = useState([]);



const fetchLoyaltyTransactions = async () => {
  setLoyaltyLoading(true);
  try {
    const res = await customerService.getCustomerLoyaltyTransactions(customer.id);

    setLoyaltySummary(res.summary);

    setLoyaltyTransactions(
      res.transactions.content.map(txn => ({
        id: txn.id,
        date: txn.date,
        description: txn.description,
        points: txn.points,
        type: txn.type === 'EARN' ? 'earned' : 'redeemed',
        expiryDate: txn.expiryDate,
        referenceType: txn.referenceType,
        referenceId: txn.referenceId,
      }))
    );
  } catch (e) {
    console.error('Failed to load loyalty transactions', e);
  } finally {
    setLoyaltyLoading(false);
  }
};

  useEffect(() => {
  if (isOpen && customer?.id) {
    fetchCustomerProfile();
    fetchLoyaltyTransactions();
  } else {
    setCustomerProfile(null);
    setExpandedOrderId(null);
    setLoyaltySummary(null);
    setLoyaltyTransactions([]);
  }
}, [isOpen, customer?.id]);


  // Dummy loyalty points history (will be replaced with API later)
  // const dummyLoyaltyHistory = [
  //   { id: 1, type: 'earned', points: 150, description: 'Order #BILL-2024-001', date: '2024-12-18T14:30:00' },
  //   { id: 2, type: 'redeemed', points: 100, description: 'Discount redemption', date: '2024-12-15T19:45:00' },
  //   { id: 3, type: 'earned', points: 75, description: 'Order #BILL-2024-002', date: '2024-12-10T12:15:00' },
  //   { id: 4, type: 'earned', points: 200, description: 'Birthday bonus', date: '2024-12-05T10:00:00' },
  //   { id: 5, type: 'redeemed', points: 50, description: 'Free dessert', date: '2024-11-28T20:30:00' },
  //   { id: 6, type: 'earned', points: 120, description: 'Order #BILL-2024-003', date: '2024-11-20T13:00:00' },
  //   { id: 7, type: 'earned', points: 80, description: 'Referral bonus', date: '2024-11-15T11:30:00' },
  //   { id: 8, type: 'redeemed', points: 200, description: 'Discount redemption', date: '2024-11-10T18:00:00' },
  // ];

  // Fetch customer profile when modal opens
  // useEffect(() => {
  //   if (isOpen && customer?.id) {
  //     fetchCustomerProfile();
  //   } else {
  //     setCustomerProfile(null);
  //     setExpandedOrderId(null);
  //   }
  // }, [isOpen, customer?.id]);

  const fetchCustomerProfile = async () => {
    setProfileLoading(true);
    try {
      const profile = await customerService.getCustomerProfile(customer.id);
      setCustomerProfile(profile);
    } catch (error) {
      console.error("Failed to fetch customer profile:", error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Helpers
  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (isoDate) => {
    if (!isoDate) return "—";
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatLoyalty = (v) => {
    const n = Number(v);
    if (isNaN(n)) return "0";
    return n.toLocaleString();
  };

  const prettyCustomerType = (type) => {
    if (!type) return "Regular";
    if (type.toLowerCase() === "vip") return "VIP";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (!isOpen || !customer) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full mx-4 overflow-hidden animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Gradient */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <User className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {customer.name}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      customer.customerType === "vip"
                        ? "bg-amber-100 text-amber-700"
                        : customer.customerType === "corporate"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-white/20 text-white"
                    }`}
                  >
                    {prettyCustomerType(customer.customerType)}
                  </span>
                  {customer.loyaltyPoints > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-white/90">
                      <Coins className="w-3.5 h-3.5 text-amber-300" />
                      {formatLoyalty(customer.loyaltyPoints)} pts
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Body - Three Column Layout */}
        <div className="flex flex-col md:flex-row max-h-[70vh]">
          {/* Left Column - Customer Info */}
          <div className="md:w-1/3 px-5 py-5 space-y-5 overflow-y-auto border-r border-gray-100 teal-scrollbar">
            {/* Contact Info */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-900">
                      {customer.phone || "—"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900 break-all">
                      {customer.email || "—"}
                    </p>
                  </div>
                </div>

                {customer.address && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-medium text-gray-900 whitespace-pre-line">
                        {customer.address}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Details */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Additional Details
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <p className="text-xs">Date of Birth</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(customer.dateOfBirth)}
                  </p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Gift className="w-3.5 h-3.5" />
                    <p className="text-xs">Anniversary</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(customer.anniversaryDate)}
                  </p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <CreditCard className="w-3.5 h-3.5" />
                    <p className="text-xs">Payment Mode</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {customer.preferredPaymentMode || "—"}
                  </p>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    <p className="text-xs">Loyalty Points</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatLoyalty(customer.loyaltyPoints)}
                  </p>
                </div>
              </div>

              {customer.dietaryPreferences && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <UtensilsCrossed className="w-3.5 h-3.5" />
                    <p className="text-xs">Dietary Preferences</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {customer.dietaryPreferences}
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            {customer.notes && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Notes
                </h3>
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-sm text-amber-900 whitespace-pre-line">
                    {customer.notes}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Center Column - Purchase History */}
          <div className="md:w-1/3 px-5 py-5 bg-gray-50/50 flex flex-col overflow-hidden border-r border-gray-100">
            {profileLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
            ) : customerProfile ? (
              <>
                {/* Purchase Summary Cards - Always visible */}
                <div className="space-y-3 flex-shrink-0">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Purchase Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <ShoppingBag className="w-4 h-4 text-teal-600" />
                        <p className="text-xs font-medium">Total Orders</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">
                        {customerProfile.purchaseSummary?.totalOrders || 0}
                      </p>
                    </div>

                    <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <IndianRupee className="w-4 h-4 text-green-600" />
                        <p className="text-xs font-medium">Total Spent</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(customerProfile.purchaseSummary?.totalAmount)}
                      </p>
                    </div>

                    <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <p className="text-xs font-medium">Avg. Order</p>
                      </div>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(customerProfile.purchaseSummary?.averageOrderValue)}
                      </p>
                    </div>

                    <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Clock className="w-4 h-4 text-purple-600" />
                        <p className="text-xs font-medium">Last Order</p>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {formatDate(customerProfile.purchaseSummary?.lastOrderDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recent Orders - Scrollable with Collapsible Cards */}
                <div className="mt-4 flex-1 flex flex-col min-h-0">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex-shrink-0">
                    Recent Orders ({customerProfile.recentOrders?.length || 0})
                  </h3>
                  {customerProfile.recentOrders?.length > 0 ? (
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1 teal-scrollbar">
                      {customerProfile.recentOrders.map((order, idx) => {
                        const isExpanded = expandedOrderId === order.orderId;
                        const itemCount = order.items?.length || 0;

                        return (
                          <div
                            key={idx}
                            className={`bg-white rounded-xl border shadow-sm transition-all ${
                              isExpanded ? 'border-teal-400 ring-1 ring-teal-100' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {/* Order Header - Clickable */}
                            <button
                              onClick={() => setExpandedOrderId(isExpanded ? null : order.orderId)}
                              className="w-full p-3 text-left"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Receipt className="w-4 h-4 text-teal-600" />
                                  <span className="text-sm font-semibold text-gray-900">
                                    {order.orderId}
                                  </span>
                                  {itemCount > 0 && (
                                    <span className="text-xs bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded-full font-medium">
                                      {itemCount} items
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    order.status === 'completed' || order.status === 'paid'
                                      ? 'bg-green-100 text-green-700'
                                      : order.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {order.status}
                                  </span>
                                  <ChevronRight
                                    className={`w-4 h-4 text-gray-400 transition-transform ${
                                      isExpanded ? 'rotate-90' : ''
                                    }`}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center justify-between mt-1.5 text-sm">
                                <span className="text-gray-500 text-xs">
                                  {formatDateTime(order.orderDate)}
                                </span>
                                <span className="font-semibold text-gray-900">
                                  {formatCurrency(order.amount)}
                                </span>
                              </div>
                            </button>

                            {/* Expanded Items Section */}
                            {isExpanded && order.items && order.items.length > 0 && (
                              <div className="px-3 pb-3 border-t border-gray-100">
                                <div className="mt-2 max-h-40 overflow-y-auto thin-scrollbar">
                                  <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-gray-50">
                                      <tr className="text-gray-500">
                                        <th className="text-left py-1.5 px-2 font-medium">Item</th>
                                        <th className="text-center py-1.5 px-2 font-medium w-12">Qty</th>
                                        <th className="text-right py-1.5 px-2 font-medium w-20">Price</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                      {order.items.map((item, i) => (
                                        <tr key={i} className="text-gray-700">
                                          <td className="py-1.5 px-2 truncate max-w-[140px]" title={item.name}>
                                            {item.name}
                                          </td>
                                          <td className="py-1.5 px-2 text-center font-medium">
                                            {item.quantity}
                                          </td>
                                          <td className="py-1.5 px-2 text-right font-medium">
                                            {formatCurrency(item.price)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
                      <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No orders yet</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">Failed to load purchase history</p>
              </div>
            )}
          </div>

          {/* Right Column - Loyalty Points History */}
          <div className="md:w-1/3 px-5 py-5 bg-white flex flex-col overflow-hidden">
            {/* Loyalty Summary - Compact Header */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Award className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Available Balance</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatLoyalty(loyaltySummary?.availableBalance || 0)} <span className="text-xs font-normal text-gray-500">pts</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total Earned</p>
                <p className="text-sm font-semibold text-green-600">
                  +{formatLoyalty(
                    loyaltyTransactions
                      .filter(h => h.type === 'earned')
                      .reduce((sum, h) => sum + h.points, 0)
                  )}
                </p>
              </div>
            </div>

            {/* Points History Table */}
            <div className="flex-1 flex flex-col min-h-0 border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Transaction History
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto thin-scrollbar">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                    <tr className="text-xs text-gray-500">
                      <th className="text-left py-2 px-3 font-medium">Date</th>
                      <th className="text-left py-2 px-3 font-medium">Description</th>
                      <th className="text-right py-2 px-3 font-medium">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loyaltyTransactions.map((entry, idx) => (
                      <tr
                        key={entry.id}
                        className={`border-b border-gray-50 hover:bg-gray-50/50 ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="py-2.5 px-3 text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(entry.date)}
                        </td>
                        <td className="py-2.5 px-3">
                          <p className="text-xs text-gray-700 truncate max-w-[130px]" title={entry.description}>
                            {entry.description}
                          </p>
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                            entry.type === 'earned'
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            {entry.type === 'earned' ? (
                              <Plus className="w-3 h-3" />
                            ) : (
                              <Minus className="w-3 h-3" />
                            )}
                            {entry.points}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Summary Footer */}
              <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex-shrink-0">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">
                    {loyaltyTransactions.length} transactions
                  </span>
                  <div className="flex gap-3">
                    <span className="text-green-600 font-medium">
                      +{formatLoyalty(
                        loyaltyTransactions
                          .filter(h => h.type === 'earned')
                          .reduce((sum, h) => sum + h.points, 0)
                      )} earned
                    </span>
                    <span className="text-red-600 font-medium">
                      -{formatLoyalty(
                        loyaltyTransactions
                          .filter(h => h.type === 'redeemed')
                          .reduce((sum, h) => sum + h.points, 0)
                      )} redeemed
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {showEditButton && onEdit && (
            <button
              onClick={() => {
                onEdit(customer);
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit Customer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
